import React, { useState } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';

const JobPreferencesSection = ({
  jobSearchStatus = '',
  onSave,
  isLoading = false
}) => {
  const { isDarkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [tempStatus, setTempStatus] = useState(jobSearchStatus);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const jobStatusOptions = [
    {
      id: 'actively-looking',
      label: 'Yes, I\'m actively looking for a new job',
      description: 'Ready to explore new opportunities and make a move soon'
    },
    {
      id: 'open-to-opportunities',
      label: 'Not really, but would consider the right opportunity',
      description: 'Happy where I am but open to exceptional opportunities'
    },
    {
      id: 'not-interested',
      label: 'No, I\'m not interested right now',
      description: 'Focused on my current role and not looking to change'
    }
  ];

  const getStatusLabel = (statusId) => {
    const option = jobStatusOptions.find(opt => opt.id === statusId);
    return option ? option.label : 'Not specified';
  };

  const getStatusDescription = (statusId) => {
    const option = jobStatusOptions.find(opt => opt.id === statusId);
    return option ? option.description : '';
  };

  const handleEdit = () => {
    setTempStatus(jobSearchStatus);
    setIsEditing(true);
    setError('');
  };

  const handleCancel = () => {
    setTempStatus(jobSearchStatus);
    setIsEditing(false);
    setError('');
  };

  const handleSave = async () => {
    if (!tempStatus) {
      setError('Please select your job search status');
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      await onSave('jobSearchStatus', tempStatus);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving job search status:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOptionSelect = (optionId) => {
    setTempStatus(optionId);
    setError('');
  };

  return (
    <div id="job-preferences-section" className="mb-8">
      {/* Section Header */}
      <div className="mb-6">
        <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} mb-2`}>
          Job Search Preferences
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Help us understand your current job search status (visible only to you and admins)
        </p>
      </div>

      {/* Content Container */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'} 
        rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} 
        p-6 sm:p-8 shadow-sm
      `}>
        <div className="space-y-6">
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wide`}>
            Current Status
          </h3>

          {!isEditing ? (
            // Display Mode
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className={`text-base font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>
                  {getStatusLabel(jobSearchStatus)}
                </div>
                {jobSearchStatus && (
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {getStatusDescription(jobSearchStatus)}
                  </div>
                )}
                {!jobSearchStatus && (
                  <div className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} italic`}>
                    Click to set your job search status
                  </div>
                )}
              </div>
              <button
                onClick={handleEdit}
                disabled={isLoading}
                className={`
                  ml-4 p-2 rounded-lg transition-all duration-200
                  ${isDarkMode 
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
                title="Edit job search status"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            // Edit Mode
            <div className="space-y-6">
              <div className="space-y-4">
                {jobStatusOptions.map((option) => (
                  <label
                    key={option.id}
                    className={`relative flex cursor-pointer rounded-xl px-4 py-4 border-2 transition-all duration-200 ${
                      tempStatus === option.id
                        ? `border-purple-500 ${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'} ring-2 ring-purple-500 ring-opacity-20`
                        : `${isDarkMode ? 'border-gray-600 hover:border-purple-400 hover:bg-gray-700/50' : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'}`
                    }`}
                  >
                    <div className="flex w-full items-center">
                      <div className="flex items-center mr-4">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                            tempStatus === option.id
                              ? 'border-purple-500 bg-purple-500'
                              : `${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`
                          }`}
                        >
                          {tempStatus === option.id && (
                            <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className={`text-base font-medium mb-1 ${
                          tempStatus === option.id
                            ? `${isDarkMode ? 'text-purple-100' : 'text-purple-900'}`
                            : `${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`
                        }`}>
                          {option.label}
                        </p>
                        <p className={`text-sm ${
                          tempStatus === option.id
                            ? `${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`
                            : `${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`
                        }`}>
                          {option.description}
                        </p>
                      </div>
                    </div>
                    
                    <input
                      type="radio"
                      name="jobSearchStatus"
                      value={option.id}
                      checked={tempStatus === option.id}
                      onChange={() => handleOptionSelect(option.id)}
                      className="sr-only"
                    />
                  </label>
                ))}
              </div>

              {error && (
                <div className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'} bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2`}>
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSave}
                  disabled={isSaving || isLoading}
                  className={`
                    inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                    ${isDarkMode 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                    focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                  `}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleCancel}
                  disabled={isSaving || isLoading}
                  className={`
                    inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                    ${isDarkMode 
                      ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobPreferencesSection; 